"use client";

import Link from "next/link";
import { Ambulance, Users, Building2, Briefcase, ChevronRight, ShieldCheck, FileText, MapPin, ClipboardList, UserCheck, CalendarDays } from "lucide-react";
import { useTenant } from "@/hooks/useTenant";
import { cn } from "@/lib/utils";

export default function TenantHub() {
  const tenant = useTenant();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 flex flex-col">

      {/* Header */}
      <header className="flex items-center gap-3 p-6">
        <div className={cn(
          "flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br shadow-lg",
          tenant.accentFrom, tenant.accentTo,
        )}>
          <Ambulance className="h-6 w-6 text-white" />
        </div>
        <div>
          <p className="text-white font-bold text-base leading-tight">{tenant.orgName}</p>
          <p className="text-slate-400 text-xs">{tenant.tagline}</p>
        </div>
      </header>

      {/* Main */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 py-12">
        <div className="max-w-4xl w-full space-y-8 text-center">

          <div>
            <h1 className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight">
              Welcome to<br />
              <span className={cn("bg-gradient-to-r bg-clip-text text-transparent", tenant.accentFrom, tenant.accentTo)}>
                {tenant.orgName}
              </span>
            </h1>
            <p className="mt-4 text-slate-400 text-lg">
              How are you accessing today?
            </p>
          </div>

          {/* Three doors */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 text-left">

            {/* Staff */}
            <Link href="/login" className="group relative rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/20 p-7 transition-all duration-200 flex flex-col gap-4">
              <div className={cn(
                "h-12 w-12 rounded-xl bg-gradient-to-br flex items-center justify-center shadow-lg",
                tenant.accentFrom, tenant.accentTo,
              )}>
                <Users className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-white font-bold text-lg">Staff Portal</p>
                <p className="text-slate-400 text-sm mt-1">
                  Clinical teams, operations, management &amp; HR
                </p>
              </div>
              <div className="flex items-center gap-1.5 text-sm font-semibold text-white/60 group-hover:text-white transition-colors mt-auto">
                Sign in <ChevronRight className="h-4 w-4" />
              </div>
              <div className="mt-3 space-y-1.5">
                {[
                  { icon: ShieldCheck, text: "Operations & briefings" },
                  { icon: FileText,    text: "Patient reports & clinical" },
                  { icon: MapPin,      text: "Live operational map" },
                ].map(({ icon: I, text }) => (
                  <div key={text} className="flex items-center gap-2 text-xs text-slate-500">
                    <I className="h-3 w-3" />{text}
                  </div>
                ))}
              </div>
            </Link>

            {/* Client */}
            <Link href="/portal/login" className="group relative rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/20 p-7 transition-all duration-200 flex flex-col gap-4">
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center shadow-lg">
                <Building2 className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-white font-bold text-lg">Client Portal</p>
                <p className="text-slate-400 text-sm mt-1">
                  Event bookings, invoices &amp; incident reports
                </p>
              </div>
              <div className="flex items-center gap-1.5 text-sm font-semibold text-white/60 group-hover:text-white transition-colors mt-auto">
                Sign in <ChevronRight className="h-4 w-4" />
              </div>
              <div className="mt-3 space-y-1.5">
                {[
                  { icon: FileText,    text: "Your event bookings" },
                  { icon: ShieldCheck, text: "Invoices & payments" },
                  { icon: MapPin,      text: "Incident reports" },
                ].map(({ icon: I, text }) => (
                  <div key={text} className="flex items-center gap-2 text-xs text-slate-500">
                    <I className="h-3 w-3" />{text}
                  </div>
                ))}
              </div>
            </Link>

            {/* Join Us */}
            <Link href="/apply" className="group relative rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/20 p-7 transition-all duration-200 flex flex-col gap-4">
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-700 flex items-center justify-center shadow-lg">
                <Briefcase className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-white font-bold text-lg">Join Our Team</p>
                <p className="text-slate-400 text-sm mt-1">
                  Apply to work with us — clinical, operational &amp; support roles
                </p>
              </div>
              <div className="flex items-center gap-1.5 text-sm font-semibold text-white/60 group-hover:text-white transition-colors mt-auto">
                Apply now <ChevronRight className="h-4 w-4" />
              </div>
              <div className="mt-3 space-y-1.5">
                {[
                  { icon: UserCheck,      text: "Paramedics, ECAs & responders" },
                  { icon: ClipboardList,  text: "Firefighter & support roles" },
                  { icon: CalendarDays,   text: "Event & operational crew" },
                ].map(({ icon: I, text }) => (
                  <div key={text} className="flex items-center gap-2 text-xs text-slate-500">
                    <I className="h-3 w-3" />{text}
                  </div>
                ))}
              </div>
            </Link>

          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="text-center p-6 text-slate-600 text-xs">
        {tenant.orgName} · Powered by OpsCom
      </footer>
    </div>
  );
}
